import { html, css, PropertyValueMap } from "lit";
import { customElement, property } from "lit/decorators.js";
import { debounce } from "ts-debounce";
import { SplitContainerItem, ComponentStore, ComponentId, Layout, dom } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { Code } from "@omegagrid/code";
import { Grid, ColumnSource, RowSource, CellType } from "@omegagrid/grid";
import { ContainerTabItem, TabEvent } from "@omegagrid/tabs";
import { AppComponent } from "@reporting/rp-app-host";
import { CsvDescription } from "@reporting/rp-utils-csv";
import { Thread, MetadataTableColumn } from "@reporting/rp-types/etl";
import { IOValidationMessage, IOValidationResult } from "../types";
import { CsvRowIssue, splitCsvLines, validateCsv } from "../csv";

const PREVIEW_DEBOUNCE = 500;
const MAX_PREVIEW_LINES = 1000;

const getCellTypeFromTableColumnType = (type: string): CellType => {
	type = type?.toLowerCase() ?? '';
	if (/int|decimal|numeric|float|double/.test(type)) return 'n';
	if (/bool/.test(type)) return 'b';
	return 't';
};

@customElement('rp-io-inputcontainer')
export class IOInputContainer extends AppComponent {

	static styles = css`
		:host {
			display: flex;
			flex-direction: column;
			width: 100%;
			height: 100%;
			position: relative;
			display: block;
		}
	`;

	@property({type: Object})
	thread: Thread;

	@property({type: Object})
	csvDescription: CsvDescription = { header: false, delimiter: '\t', quote: '"', decimalSeparator: ',' };

	@property({type: Array})
	tableColumns: MetadataTableColumn[] = [];

	@property({type: Array})
	items: SplitContainerItem[] = [{id: 'input', size: null}, {id: 'preview', size: null}];

	/** Mobile layout: the editor and the preview as tabs instead of a vertical split. */
	@property({type: Boolean, reflect: true})
	compact = false;

	@property({type: Array})
	tabItems: ContainerTabItem[] = [
		{ id: 'input', title: msg('Input'), preventDisconnect: true },
		{ id: 'preview', title: msg('Preview'), preventDisconnect: true },
	];

	private input: Code;
	private _pendingContent: string | null = null;

	private preview: Grid;
	private _columns: ColumnSource[];
	private _previewRows: RowSource[] = [];
	private _previewTruncated = false;

	componentStore = new ComponentStore(async (id: ComponentId) => {
		switch (id) {
			case 'input': {
				this.input = dom.createElement<Code>('og-code') as Code;
				this.input.language = 'text/plain';
				this.input.options = {
					minimap: { enabled: false },
					tabCompletion: 'off',
					wordBasedSuggestions: 'off',
					trimAutoWhitespace: false,
					detectIndentation: false,
					insertSpaces: false,
				};
				this.input.addEventListener('change', () => this.debouncePreview());
				if (this.compact) this.input.style.height = '100%'; // the tab container does not size its components
				if (this._pendingContent != null) {
					this.input.content = this._pendingContent;
					this._pendingContent = null;
					this.debouncePreview();
				}
				return this.input;
			}
			case 'preview': {
				this.preview = dom.createElement<Grid>('og-grid') as Grid;
				this.preview.updateOptions({ colLetters: false });
				if (this.compact) this.preview.style.height = '100%';
				this._applyPreview(); // in the tab layout the grid may be created after the content was parsed
				return this.preview;
			}
		}
	});

	private _resizeObserver: ResizeObserver;

	firstUpdated() {
		// in the mobile layout the controls above change height (parameters load, messages appear, sections fold),
		// and the editor keeps its Monaco container in document.body, so it must be laid out again on every size change
		if (this.compact) {
			this._resizeObserver = new ResizeObserver(() => this.layout());
			this._resizeObserver.observe(this);
		}
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this._resizeObserver?.disconnect();
		this._resizeObserver = null;
	}

	updated(changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
		if (changedProperties.has('csvDescription') || changedProperties.has('tableColumns')) {
			this._columns = null;
			this.debouncePreview();
		}
	}

	setEditorContent(content: string) {
		if (this.input) {
			this.input.content = content ?? '';
			this.debouncePreview();
		} else {
			this._pendingContent = content ?? '';
		}
	}

	getEditorContent(): string | null {
		return this.input?.content.length > 0 ? this.input.content : null;
	}

	debouncePreview = debounce(this.updatePreviewData, PREVIEW_DEBOUNCE);

	async updatePreviewData() {
		if (!this.csvDescription) return;

		const lines = splitCsvLines(this.input?.content ?? '');
		if (lines.length === 0) {
			this._previewRows = [];
			this._previewTruncated = false;
			this._applyPreview();
			this._dispatchValidation(false, [{
				id: 'empty',
				type: 'hint',
				text: msg('No input data. Select a file or paste the content into the editor.'),
			}]);
			return;
		}
		this.dispatchEvent(new CustomEvent('edit', { bubbles: true, composed: true }));

		const columns = this.tableColumns.map(c => c.column);
		const validation = validateCsv(lines, this.csvDescription, columns, MAX_PREVIEW_LINES);
		const dataLineCount = lines.length - (this.csvDescription.header ? 1 : 0);
		const truncated = dataLineCount > MAX_PREVIEW_LINES;

		this._previewRows = validation.rows.map(cells => {
			const data: Record<number, string> = {};
			cells.forEach((v, i) => data[i] = v);
			return { data };
		});
		this._previewTruncated = truncated;
		this._applyPreview();

		// "(Row 12: 2/6, +3)" = first offending editor line, cells found / expected, further offending rows
		const where = ({ count, firstLine, firstFound }: CsvRowIssue) =>
			`(${msg('Row')} ${firstLine}: ${firstFound}/${columns.length}${count > 1 ? `, +${count - 1}` : ''})`;

		const messages: IOValidationMessage[] = [];
		if (validation.shortRows) {
			messages.push({
				id: 'columns',
				type: 'danger',
				text: `${msg('Some rows have fewer columns than expected.')} ${where(validation.shortRows)}`,
			});
		}
		if (validation.longRows) {
			messages.push({
				id: 'extraColumns',
				type: 'warning',
				text: `${msg('Some rows have more columns than expected.')} ${where(validation.longRows)}`,
			});
		}
		if (validation.header) {
			const { index, found, expected } = validation.header;
			messages.push({
				id: 'header',
				type: 'warning',
				text: `${msg('Header row does not match expected columns.')} (${index + 1}: "${found ?? ''}" ≠ "${expected}")`,
			});
		}
		if (truncated) {
			messages.push({
				id: 'truncated',
				type: 'info',
				text: `${msg('Preview data has been truncated.')} (max ${MAX_PREVIEW_LINES})`,
			});
		}

		// the loader maps cells to columns by position, so only a short row blocks the import; extra cells and a header mismatch are warnings
		this._dispatchValidation(!validation.shortRows, messages);
	}

	private _dispatchValidation(valid: boolean, messages: IOValidationMessage[]) {
		this.dispatchEvent(new CustomEvent<IOValidationResult>('io.validate', {
			detail: { valid, messages },
			bubbles: true,
			composed: true,
		}));
	}

	private _applyPreview() {
		if (!this.preview) return;
		// filtering and sorting only make sense while the preview holds every row
		this.preview.updateOptions({
			filters: { enabled: !this._previewTruncated },
			columnSorting: !this._previewTruncated,
		});
		this._setGridSourceData(this._previewRows);
	}

	_setGridSourceData(rows: RowSource[]) {
		if (!this.preview) return;
		if (!this._columns) {
			this._columns = this.tableColumns.map((c, i) => ({
				header: c.column,
				data: i,
				t: getCellTypeFromTableColumnType(c.type)
			}));
		}
		this.preview.setSourceData({ columns: this._columns, rows });
	}

	/** The tab container lays out only the first component it shows; the shown tab needs its layout and the hidden editor must drop its body container. */
	private _onTabSelect = async (e: TabEvent) => {
		const component = await this.componentStore.get(e.tab.id) as Layout | null;
		await (component as any)?.updateComplete;
		requestAnimationFrame(() => this.layout());
	};

	layout() {
		this.input?.layout();
		this.preview?.layout();
	}

	render = () => this.compact ? html`
		<og-tabcontainer
			cacheComponents
			tabSize="stretch"
			.createComponent="${(id: ComponentId) => this.componentStore.get(id)}"
			.items="${this.tabItems}"
			@tab.select="${this._onTabSelect}"
			style="height: 100%;">
		</og-tabcontainer>
	` : html`
        <og-splitcontainer
			gap="5"
			orientation="vertical"
			.createComponent="${(id: ComponentId) => this.componentStore.get(id)}"
			.items="${this.items}"
			style="height: 100%;">
		</og-splitcontainer>
	`;
}
